// components/ReplyAccountEmailModal.jsx

import React, { useState, useEffect } from "react";
import { X, Send, Reply } from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const ReplyAccountEmailModal = ({
  accountData,
  originalEmail,
  onClose,
  onSuccess,
}) => {
  const { isDarkMode } = useTheme();
  const [formData, setFormData] = useState({
    body: "",
    bodyHtml: "",
    cc: "",
    bcc: "",
    trackOpens: true,
    trackLinks: true,
  });
  const [loading, setLoading] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);

  useEffect(() => {
    checkEmailConnection();
    initializeReplyData();
  }, [originalEmail]);

  const checkEmailConnection = async () => {
    try {
      const response = await apiService.getEmailConnectionStatus();
      setEmailStatus(response.data);
    } catch (error) {
      console.error("Error checking email connection:", error);
      setEmailStatus({ connected: false });
    }
  };

  const initializeReplyData = () => {
    if (!originalEmail) return;

    const quotedBody = buildQuotedReply(originalEmail);

    setFormData((prev) => ({
      ...prev,
      body: quotedBody,
      bodyHtml: quotedBody,
    }));
  };

  const buildQuotedReply = (email) => {
    const date = new Date(email.sentAt || email.createdAt).toLocaleString();
    const from = email.from;

    const senderName = email.sender
      ? `${email.sender.firstName || ""} ${email.sender.lastName || ""}`.trim() ||
        email.from
      : email.from;

    const originalBody = email.bodyHtml || email.body;

    const isNestedReply =
      email.metadata?.isReply || email.subject?.startsWith("Re:");
    const replyDepth = email.metadata?.replyDepth || 0;

    return `


------- Original Message -------
From: ${senderName} <${from}>
Date: ${date}
Subject: ${email.subject}
${isNestedReply ? `[Reply depth: ${replyDepth + 1}]` : ""}

${originalBody}`;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.body.trim()) {
      alert("Please enter a reply message");
      return;
    }

    if (!emailStatus?.connected) {
      alert("Email is not connected. Please connect your email in settings.");
      return;
    }

    setLoading(true);

    try {
      const replyData = {
        body: formData.body,
        bodyHtml: formData.bodyHtml || formData.body,
        cc: formData.cc ? formData.cc.split(",").map((e) => e.trim()) : [],
        bcc: formData.bcc ? formData.bcc.split(",").map((e) => e.trim()) : [],
        trackOpens: formData.trackOpens,
        trackLinks: formData.trackLinks,
      };

      await apiService.replyAccountEmail(
        accountData.id,
        originalEmail.id,
        replyData,
      );

      alert("Reply sent successfully!");
      onSuccess();
    } catch (error) {
      console.error("Error sending reply:", error);
      alert(
        error.response?.data?.message ||
          "Failed to send reply. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const getReplySubject = () => {
    const subject = originalEmail?.subject || "";
    return subject.startsWith("Re:") ? subject : `Re: ${subject}`;
  };

  const getReplyTo = () => {
    return originalEmail?.from || originalEmail?.to || "";
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div
        className={`border rounded-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col ${
          isDarkMode
            ? "bg-[#0C0E12] border-gray-700"
            : "bg-white border-gray-300"
        }`}
      >
        <div
          className={`flex items-center justify-between p-6 border-b ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div>
            <h2
              className={`text-lg font-semibold flex items-center gap-2 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              <Reply size={20} />
              Reply to Email
            </h2>
            <p
              className={`text-sm mt-1 ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              {accountData?.name}
            </p>
            {emailStatus && (
              <p
                className={`text-sm mt-1 ${
                  emailStatus.connected
                    ? isDarkMode
                      ? "text-green-400"
                      : "text-green-600"
                    : isDarkMode
                      ? "text-yellow-400"
                      : "text-yellow-600"
                }`}
              >
                {emailStatus.connected
                  ? `Sending from: ${emailStatus.email}`
                  : "Email not connected"}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className={`transition-colors ${
              isDarkMode
                ? "text-gray-400 hover:text-white"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-4">
            {/* Original Email Info */}
            <div
              className={`border rounded-lg p-4 ${
                isDarkMode
                  ? "bg-gray-800/50 border-gray-700"
                  : "bg-gray-50 border-gray-200"
              }`}
            >
              <div className="text-sm space-y-1">
                <div className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
                  <strong
                    className={isDarkMode ? "text-white" : "text-gray-900"}
                  >
                    To:
                  </strong>{" "}
                  {getReplyTo()}
                </div>
                <div className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
                  <strong
                    className={isDarkMode ? "text-white" : "text-gray-900"}
                  >
                    Subject:
                  </strong>{" "}
                  {getReplySubject()}
                </div>
                {originalEmail?.cc && originalEmail.cc.length > 0 && (
                  <div
                    className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                  >
                    <strong
                      className={isDarkMode ? "text-white" : "text-gray-900"}
                    >
                      Original CC:
                    </strong>{" "}
                    {originalEmail.cc.join(", ")}
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  CC
                </label>
                <input
                  type="text"
                  name="cc"
                  value={formData.cc}
                  onChange={handleChange}
                  placeholder="email1@example.com, email2@example.com"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#13161B] border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  BCC
                </label>
                <input
                  type="text"
                  name="bcc"
                  value={formData.bcc}
                  onChange={handleChange}
                  placeholder="email1@example.com, email2@example.com"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    isDarkMode
                      ? "bg-[#13161B] border-gray-700 text-white placeholder-gray-500 focus:border-blue-500"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500"
                  }`}
                />
              </div>
            </div>

            <div>
              <label
                className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                Reply Message <span className="text-red-500">*</span>
              </label>
              <textarea
                name="body"
                value={formData.body}
                onChange={handleChange}
                required
                rows={16}
                className={`w-full px-3 py-2 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm ${
                  isDarkMode
                    ? "bg-[#13161B] border-gray-700 text-white focus:border-blue-500"
                    : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                }`}
              />
              <p
                className={`text-xs mt-1 ${
                  isDarkMode ? "text-gray-500" : "text-gray-400"
                }`}
              >
                Type your reply above the quoted original message
              </p>
            </div>

            <div className="flex gap-4">
              <label
                className={`flex items-center gap-2 text-sm cursor-pointer ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                <input
                  type="checkbox"
                  name="trackOpens"
                  checked={formData.trackOpens}
                  onChange={handleChange}
                  className={`rounded focus:ring-blue-500 ${
                    isDarkMode
                      ? "border-gray-600 text-blue-500"
                      : "border-gray-300 text-blue-600"
                  }`}
                />
                Track Opens
              </label>
              <label
                className={`flex items-center gap-2 text-sm cursor-pointer ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                <input
                  type="checkbox"
                  name="trackLinks"
                  checked={formData.trackLinks}
                  onChange={handleChange}
                  className={`rounded focus:ring-blue-500 ${
                    isDarkMode
                      ? "border-gray-600 text-blue-500"
                      : "border-gray-300 text-blue-600"
                  }`}
                />
                Track Link Clicks
              </label>
            </div>
          </div>

          <div
            className={`p-6 border-t flex justify-end gap-3 ${
              isDarkMode ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <button
              type="button"
              onClick={onClose}
              className={`px-4 py-2 rounded-lg transition-colors ${
                isDarkMode
                  ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !emailStatus?.connected}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                isDarkMode
                  ? "bg-blue-600 hover:bg-blue-700 text-white"
                  : "bg-blue-500 hover:bg-blue-600 text-white"
              }`}
            >
              <Send size={16} />
              {loading ? "Sending..." : "Send Reply"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReplyAccountEmailModal;
